import { useNavigate } from 'react-router-dom';

const BrochureCoverPage = () => {
  const navigate = useNavigate();

  const handleDownload = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Navigation - Hidden when printing */}
      <div className="print:hidden fixed top-0 left-0 right-0 z-[9999] bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-[#2F2F2F] py-4 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/brochure')}
              className="flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-semibold">Back to Full Brochure</span>
            </button>
            
            <button
              onClick={handleDownload}
              className="px-8 py-3 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide text-sm flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span>Download Cover Page</span>
            </button>
          </div>
          
          {/* Instructions */}
          <div className="bg-[#00E600]/10 border border-[#00E600]/30 rounded-md px-4 py-2">
            <p className="text-[#00E600] text-xs font-semibold flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              IMPORTANT: When downloading, enable "Background graphics" or "Print backgrounds" in your print settings.
            </p>
          </div>
        </div>
      </div>

      {/* Cover Page - Properly Centered */}
      <section className="min-h-screen h-screen flex items-center justify-center relative overflow-hidden bg-[#0D0D0D] py-20">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw5fHxuZXR3b3JrfGVufDB8fHx8MTczMzkyNTg5Nnww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 text-center px-8 w-full max-w-6xl flex flex-col items-center justify-center">
          {/* Glowing Logo - with top margin */}
          <div className="mb-20">
            <img 
              src="https://customer-assets.emergentagent.com/job_find-your-agent/artifacts/m94nk0ss_NYAXO.png" 
              alt="NYAXO Logo" 
              className="h-28 mx-auto"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(0, 230, 0, 0.5)) drop-shadow(0 0 40px rgba(0, 230, 0, 0.3))',
                animation: 'glow 2s ease-in-out infinite alternate'
              }}
            />
          </div>

          {/* Slogan - Centered */}
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-10 uppercase tracking-wide leading-tight max-w-5xl">
            ENGINEERING THE FUTURE<br />
            <span className="text-[#00E600]" style={{
              textShadow: '0 0 20px rgba(0, 230, 0, 0.5), 0 0 40px rgba(0, 230, 0, 0.3)'
            }}>OF INTELLIGENCE</span>
          </h1>

          {/* Tagline - Centered */}
          <p className="text-xl text-[#B3B3B3] font-light tracking-wide leading-relaxed max-w-4xl">
            We design AI systems that transform corporate operations — from energy and infrastructure to SaaS.
          </p>
        </div>
      </section>

      {/* Print Styles */}
      <style>{`
        @keyframes glow {
          from {
            filter: drop-shadow(0 0 10px rgba(0, 230, 0, 0.5)) drop-shadow(0 0 20px rgba(0, 230, 0, 0.3));
          }
          to {
            filter: drop-shadow(0 0 30px rgba(0, 230, 0, 0.8)) drop-shadow(0 0 60px rgba(0, 230, 0, 0.5));
          }
        }
        
        @media print {
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            background-color: #0D0D0D !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          /* Ensure all backgrounds and colors print */
          div, section, p, h1, h2, h3, h4, h5, h6 {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          /* Page size and margins */
          @page {
            size: A4 portrait;
            margin: 0;
          }
        }
        
        @media screen {
          body {
            background-color: #0D0D0D;
          }
        }
      `}</style>
    </div>
  );
};

export default BrochureCoverPage;
